import React from "react";
import Router from "next/router";
import { compose, ensureFunc, idX, isFunc, isPromise } from "@insightfulscience/shared-utils/fn";
import { not } from "@insightfulscience/shared-utils/validation";

/** @typedef {import("next").NextPageContext} NextPageContext */
/** @typedef {import("next").NextPage} NextPage */

/**
 * @typedef {[
 *  <A extends any[]>(context: NextPageContext, ...args: A) => boolean,
 *  string | (context: NextPageContext) => string | null | undefined
 * ]} PreCondition
 */

/**
 * @template {{}} P
 * @typedef {[
 *  <A extends any[]>(props: P, context: NextPageContext, ...args: A) => boolean,
 *  string | (props: P, context: NextPageContext) => string | null | undefined
 * ]} PostCondition
 */

/**
 * @typedef {[
 *  <E extends Error, A extends any[]>(err: E, context: NextPageContext, ...args: A) => boolean,
 *  string | <E extends Error>(error: E, context: NextPageContext) => string | null | undefined
 * ]} ErrorCondition
 */

/**
 * @template {{}} P
 * @typedef {import("next").NextPage<P> & {
 * 	preConditions?: PreCondition[];
 * 	postConditions?: PostCondition<P>[];
 *  onError?: ErrorCondition[];
 * }} ConditionalPage
 */

const hooks = {
	mapServerSideRedirect: pathOrUrl => pathOrUrl,
	mapClientSideRedirect: pathOrUrl => pathOrUrl,
};

/** @type {(mapper: (pathOrUrl: string, context: import("next").NextPageContext) => string) => void} */
export const mapServerSideRedirectUrl = mapper => {
	hooks.mapServerSideRedirect = ensureFunc(mapper, pathOrUrl => pathOrUrl);
};

/** @type {(mapper: (pathOrUrl: string, context: import("next").NextPageContext) => string) => void} */
export const mapClientSideRedirectUrl = mapper => {
	hooks.mapClientSideRedirect = ensureFunc(mapper, pathOrUrl => pathOrUrl);
};

const serverReplaceUrl = (dest, context) => {
	const { res } = context;
	res.redirect(302, hooks.mapServerSideRedirect(dest, context));
	res.end();
	return { __dontRender: true };
};

const isUrlNotHasProtocol = url => url.slice(0, 2) === "//";

const withProtocol = url => (isUrlNotHasProtocol(url) ? `${window.location.protocol}${url}` : url);

const clientReplaceUrl = (dest, context) => {
	Router.push(hooks.mapClientSideRedirect(withProtocol(dest), context));
	return { __dontRender: true };
};

export const replaceUrl = typeof window === "undefined" ? serverReplaceUrl : clientReplaceUrl;

const redirect = (dest, context, args) => {
	const redirectHandler = typeof window === "undefined" ? serverReplaceUrl : clientReplaceUrl;
	return redirectHandler(
		isFunc(dest) ? dest(...args) : dest, // calculating dest
		context,
	);
};

const safeApply = (fn, ...args) => (isFunc(fn) ? fn(...args) : { __dontRender: false });

const shouldRender = value => !value?.__dontRender; // eslint-disable-line no-underscore-dangle

const createTransition = ([predicate, redirectTo]) => (...args) =>
	shouldRender(...args) && predicate(...args) ? redirectTo : null;

const composeTransitions = (t1, t2) => (...args) => t1(...args) || t2(...args);

const createTransitions = transitionRule =>
	transitionRule.map(createTransition).reduce(composeTransitions);

export const redirectIf = transitionRules => {
	const transition = createTransitions(transitionRules);
	return getInitialProps => (context, ...args) => {
		const dest = transition(context, ...args);
		return dest
			? redirect(dest, context, [context, ...args])
			: safeApply(getInitialProps, context, ...args);
	};
};

export const redirectIfProps = transitionRules => {
	const transition = createTransitions(transitionRules);
	return getInitialProps => (context, ...args) => {
		const applyRedirect = props => {
			const transitionArgs = [props, context, ...args];
			const dest = transition(...transitionArgs);
			return dest // check redirect condition
				? redirect(dest, context, transitionArgs)
				: props;
		};

		const componentProps = safeApply(getInitialProps, context, ...args);

		return isPromise(componentProps)
			? componentProps.then(applyRedirect)
			: applyRedirect(componentProps);
	};
};
export const redirectOnError = transitionRules => {
	const transition = createTransitions(transitionRules);

	return getInitialProps => (context, ...args) => {
		const applyRedirect = err => {
			const transitionArgs = [err, context, ...args];
			const dest = transition(...transitionArgs);
			if (!dest) throw err;
			return redirect(dest, context, transitionArgs);
		};

		try {
			const componentProps = safeApply(getInitialProps, context, ...args);
			return isPromise(componentProps) ? componentProps.catch(applyRedirect) : componentProps;
		} catch (err) {
			return applyRedirect(err);
		}
	};
};

const reverseCondition = ([condition, fallback]) => [not(condition), fallback];
const reverseConditions = conditions => conditions.map(reverseCondition);

const checkPreConditions = compose(redirectIf, reverseConditions);
const checkPostConditions = compose(redirectIfProps, reverseConditions);

/** @type {<P extends {}>(Page: ConditionalPage<P>) => NextPage<P>} */
const WithConditions = Page => {
	const { preConditions, postConditions, onError, getInitialProps } = Page;

	// eslint-disable-next-line no-underscore-dangle, react/destructuring-assignment, react/prop-types
	const ConditionalPage = props => ((props.__dontRender ? null : <Page {...props} />));

	Object.assign(ConditionalPage, Page);

	delete ConditionalPage.preConditions;
	delete ConditionalPage.postConditions;
	delete ConditionalPage.onError;

	// eslint-disable-next-line no-param-reassign
	ConditionalPage.getInitialProps = compose(
		preConditions ? checkPreConditions(preConditions) : idX,
		postConditions ? checkPostConditions(postConditions) : idX,
		onError ? redirectOnError(onError) : idX,
	)(getInitialProps);

	return ConditionalPage;
};

export default WithConditions;
